import { useState, useEffect } from 'react'
import {
  Dialog,
  Slide,
  Box,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  Button,
  Divider,
  IconButton,
} from '@mui/material'
import { Close, Save } from '@mui/icons-material'
import { forwardRef } from 'react'

const SlideUp = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />
})

const RATIONALE_PRESETS = [
  'New co-pack customer order',
  'Capacity constraint',
  'Safety stock adjustment',
  'Seasonal demand shift',
  'Raw material availability',
  'Equipment maintenance window',
  'Customer promotion',
  'Inventory rebalancing',
]

export default function OverrideBottomSheet({ open, onClose, onSave, editContext }) {
  const [selectedPreset, setSelectedPreset] = useState('')
  const [customRationale, setCustomRationale] = useState('')

  useEffect(() => {
    if (open) {
      setSelectedPreset('')
      setCustomRationale('')
    }
  }, [open])

  const handleSave = () => {
    const rationale = selectedPreset === 'custom'
      ? customRationale
      : selectedPreset
    onSave?.({
      ...editContext,
      rationale,
    })
    onClose()
  }

  const canSave = selectedPreset && (selectedPreset !== 'custom' || customRationale.trim())

  return (
    <Dialog
      open={open}
      onClose={onClose}
      slots={{ transition: SlideUp }}
      fullWidth
      maxWidth="sm"
      sx={{
        '& .MuiDialog-container': { alignItems: 'flex-end' },
        '& .MuiPaper-root': {
          borderRadius: '20px 20px 0 0',
          m: 0,
          maxHeight: '70vh',
        },
      }}
    >
      <Box sx={{ px: 3, py: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="h6">Override Rationale</Typography>
          <IconButton onClick={onClose} size="small">
            <Close sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>

        {editContext && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            {editContext.itemNumber} — {editContext.description} — {editContext.monthLabel} — Cases: {editContext.oldValue} → {editContext.newValue}
          </Typography>
        )}

        <Divider sx={{ mb: 2 }} />

        <Typography variant="subtitle2" gutterBottom>
          Select a reason for this override:
        </Typography>

        <RadioGroup
          value={selectedPreset}
          onChange={(e) => setSelectedPreset(e.target.value)}
        >
          {RATIONALE_PRESETS.map((preset) => (
            <FormControlLabel
              key={preset}
              value={preset}
              control={<Radio size="small" />}
              label={preset}
              sx={{ '& .MuiTypography-root': { fontSize: '0.875rem' } }}
            />
          ))}
          <FormControlLabel
            value="custom"
            control={<Radio size="small" />}
            label="Other (custom)"
            sx={{ '& .MuiTypography-root': { fontSize: '0.875rem' } }}
          />
        </RadioGroup>

        {selectedPreset === 'custom' && (
          <TextField
            value={customRationale}
            onChange={(e) => setCustomRationale(e.target.value)}
            placeholder="Enter custom rationale…"
            multiline
            rows={2}
            fullWidth
            size="small"
            sx={{ mt: 1, mb: 2 }}
          />
        )}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mt: 2, mb: 1 }}>
          <Button variant="outlined" onClick={onClose} size="small">
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!canSave}
            startIcon={<Save sx={{ fontSize: 16 }} />}
            size="small"
          >
            Save Override
          </Button>
        </Box>
      </Box>
    </Dialog>
  )
}
